import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { useNotificationContext } from 'core/states';
import { consumerRestartService } from 'dashboard/services';
import { IConsumerJob, IConsumerRestartTarget } from 'dashboard/types';

/**
 * Consumers pick up and finish work continuously, and so do the jobs they run, so
 * both lists on this page are stale the moment they render. Polled rather than
 * pushed: an admin page with a handful of viewers and two cheap reads does not
 * justify a socket.
 */
export const CONSUMER_REFETCH_INTERVAL = 30 * 1000;

export const useGetConsumerRestartTargets = (options: { enabled?: boolean } = {}) => {
  const { enabled = true } = options;

  const { data, isError, isLoading, refetch } = useQuery(
    ['consumerRestartTargets'],
    ({ signal }) => consumerRestartService.getConsumerRestartTargets(signal),
    {
      enabled,
      // Targets now carry live consumer counts, so this list goes stale the same
      // way the job list does — a button showing 2/2 for an env that died two
      // minutes ago is worse than showing no count at all.
      refetchInterval: CONSUMER_REFETCH_INTERVAL,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
    }
  );

  return {
    isError,
    isLoading,
    refetchTargets: () => refetch({ cancelRefetch: true }),
    targets: (data?.records ?? []) as IConsumerRestartTarget[],
  };
};

export const useGetConsumerJobs = (options: { enabled?: boolean } = {}) => {
  const { enabled = true } = options;

  const { data, dataUpdatedAt, isError, isFetching, isLoading, refetch } = useQuery(
    ['consumerJobs'],
    ({ signal }) => consumerRestartService.getConsumerJobs(signal),
    {
      enabled,
      // react-query keeps the previous data during a background refetch, so rows
      // do not blank out every 30 seconds.
      refetchInterval: CONSUMER_REFETCH_INTERVAL,
      // Left at the default (false): a backgrounded tab stops polling, so a page
      // someone left open overnight is not hitting the endpoint 2,880 times.
      refetchIntervalInBackground: false,
      // Overrides the app-wide `refetchOnWindowFocus: false` (src/index.tsx) for
      // this query alone. Because the interval pauses in a background tab, without
      // this you would return to a page showing however-old data and wait up to
      // another 30s for a tick — the two settings only add up to "live" together.
      refetchOnWindowFocus: true,
    }
  );

  return {
    // When the current data landed — the countdown is derived from this rather
    // than from a timer of its own, so it stays truthful when a refetch is
    // triggered off-schedule (window focus, or the manual refresh).
    dataUpdatedAt,
    isError,
    isFetching,
    isLoading,
    jobs: (data?.records ?? []) as IConsumerJob[],
    // cancelRefetch so a post-mutation refresh cannot piggyback on a poll that was
    // already in flight before the mutation — react-query v3 returns the existing
    // promise otherwise, and the just-cancelled rows would linger for another cycle.
    refetchJobs: () => refetch({ cancelRefetch: true }),
  };
};

export const useRestartConsumers = () => {
  const { t } = useTranslation();

  const { showSuccessNotification, showErrorNotification } = useNotificationContext();

  // Which env is mid-restart. useMutation exposes a single isLoading, but the page
  // renders one button per env, so the env has to be tracked separately to keep the
  // spinner on the button that was actually clicked.
  const [restartingEnv, setRestartingEnv] = useState<string | null>(null);

  const { errors } = useHandleErrors({
    loading: { message: t('messageConsumerRestartInProgress') },
    error: { title: t('errorTitleConsumerRestart') },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation(consumerRestartService.restartConsumers, {
    ...errors,
    // Overrides the generic handler on purpose: a refused env and a restart that
    // started no consumers both come back HTTP 200 with `success: false`, and the
    // BE's `message` carries the detail (including the live consumer count) that
    // the generic handler would replace with a generic serverError string.
    onSuccess: (result) => {
      if (result?.success) {
        showSuccessNotification({ message: result.message }, NotificationPosition.TOP);

        return;
      }

      showErrorNotification(
        { title: t('errorTitleConsumerRestart'), message: result?.message },
        NotificationPosition.TOP
      );
    },
  });

  const restartConsumers = useCallback(
    async (env: string) => {
      setRestartingEnv(env);

      try {
        await mutateAsync({ env });
      } finally {
        setRestartingEnv(null);
      }
    },
    [mutateAsync]
  );

  return { restartConsumers, restartingEnv };
};

export const useCancelConsumerJobs = () => {
  const { t } = useTranslation();

  const { showSuccessNotification, showErrorNotification } = useNotificationContext();

  const { errors } = useHandleErrors({
    loading: { message: t('messageCancelConsumerJobsInProgress') },
    error: { title: t('errorTitleCancelConsumerJobs') },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync, isLoading } = useMutation(consumerRestartService.cancelConsumerJobs, {
    ...errors,
    // Deliberately replaces the generic handler rather than composing with it. The
    // usual pattern calls errors.onSuccess first, but useHandleErrors branches on the
    // response's own `success` flag and would raise a second, generic toast over the
    // specific one below. Nothing is forfeited: no invalidateQueries is configured,
    // and the loading notification is replaced by whichever notification fires here.
    onSuccess: (result) => {
      if (!result?.canceled) {
        showErrorNotification(
          { title: t('errorTitleCancelConsumerJobs'), message: t('messageCancelConsumerJobsNone') },
          NotificationPosition.TOP
        );

        return;
      }

      // Reports the parts the BE went to the trouble of counting separately: rows
      // that finished between render and click, and rows that threw. Collapsing
      // these into "canceled N" is the hiding this override exists to avoid.
      const detail = [
        result.killed ? t('messageCancelConsumerJobsKilled', { count: result.killed }) : null,
        result.skipped ? t('messageCancelConsumerJobsSkipped', { count: result.skipped }) : null,
        result.failed ? t('messageCancelConsumerJobsFailed', { count: result.failed }) : null,
      ]
        .filter(Boolean)
        .join(' ');

      showSuccessNotification(
        {
          message: `${t('messageCancelConsumerJobsSuccess', {
            count: result.canceled,
          })} ${detail}`.trim(),
        },
        NotificationPosition.TOP
      );
    },
  });

  return { cancelConsumerJobs: mutateAsync, isCancelConsumerJobsLoading: isLoading };
};
